import { FC, useContext, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Table } from '@smartshell/ui-kit';
import { Column } from 'react-table';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { Combo, EPaymentItemType, TService, TShopItem } from 'types';

import { IServicesRequest, IServicesResponse, SERVICES } from './schema';
import s from './ShopServicesTable.module.scss';

interface IServicesFieldProps {
  onItemSelect: (item: TShopItem) => void;
  clientUUID?: string | null;
  search?: string;
  combo?: Combo | null;
  disabledTariff?: boolean;
  promoCode?: boolean;
}

export const ShopServicesTable: FC<IServicesFieldProps> = ({
  onItemSelect,
  clientUUID,
  search,
  combo,
  disabledTariff,
  promoCode,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const columns: Array<Column<TService>> = useMemo(
    () => [
      {
        Header: t('title'),
        accessor: 'title',
        width: 634,
      },
      {
        Header: t('cost'),
        accessor: 'cost',
        Cell: ({ value }) => `${value.toLocaleString()} ${currency.sign}`,
        width: 163,
      },
    ],
    [t]
  );

  const { data, loading } = useQuery<IServicesResponse, IServicesRequest>(
    SERVICES,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: {
        input: {
          client_uuid: clientUUID ?? undefined,
        },
      },
    }
  );

  const filteredData = useMemo(
    () =>
      search && data
        ? data.services.filter((service) =>
            service.title.toLowerCase().includes(search.toLowerCase())
          )
        : data?.services ?? [],
    [search, data]
  );

  return (
    <>
      <Table
        disabled={!!combo || disabledTariff || promoCode}
        className={s['services-table']}
        data={filteredData}
        columns={columns}
        onRowClick={(_, row) => {
          const { cost, id, use_global_discounts } = row.original;

          onItemSelect({
            id,
            cost,
            use_global_discounts,
            amount: 1,
            type: EPaymentItemType.SERVICE,
            original: row.original,
          });
        }}
        isLoading={loading}
        emptyDataPlaceholder={t('shopForm.empty.services')}
        dataTestId="services-table"
      />
    </>
  );
};
